import React from "react";
import { View, StyleSheet } from "react-native";
import BaseExtensionController from "./BaseExtensionController";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtListRow from "../../components/NtListRow";
import NtBadge from "../../components/NtBadge";
import NtModal from "../../components/NtModal";
import NtIvrMenuForm from "../components/NtIvrMenuForm";
import { fetchIvrMenus } from "../../api/ivr";
import { asyncStorageSetData, showToast } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import ivrcsvcolumndata from "../data/ivrcsvcolumndata";

class IvrController extends BaseExtensionController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    totalItems: 0,
    page: 1,
    editingItem: null,
  };

  column_location = "@ivr_column_storage";
  createRef = React.createRef();
  editRef = React.createRef();

  componentDidMount() {
    super.componentDidMount?.();
    this.handleDefaultColumnsFetch(
      this.column_location,
      ivrcsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }


  handleDataFetch = async () => {
    const domain = this.context?.user?.domain;
    if (!domain) return;
    this.setState({ loading: true });
    const res = await fetchIvrMenus(domain, this.state.page, settings.pageSizeLoad);
    Logger("IvrMenus fetch response", res);
    if (res.ok) {
      this.setState({
        data: res.data?.data || [],
        totalItems: res.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const domain = this.context?.user?.domain;
    const res = await fetchIvrMenus(domain, 1, 10000000);
    return res.ok ? res.data?.data || [] : [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "ivr_menu_enabled") {
      return item[column] === "true" || item[column] === true ? "Enabled" : "Disabled";
    }
    return item[column];
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  renderRow = (item, index) => (
    <NtListRow
      key={item.ivr_menu_uuid || item.id || index}
      item={item}
      columns={this.state.columns}
      renderCustom={(row, column) => {
        if (column === "ivr_menu_enabled") {
          const enabled = row[column] === "true" || row[column] === true;
          return (
            <NtBadge variant={enabled ? "green" : "gray"}>
              {enabled ? "Active" : "Disabled"}
            </NtBadge>
          );
        }
      }}
      onActionIcon="pencil"
      onActionPress={
        this.handlePermissionCheck(settings.permissionTypeEdit)
          ? () =>
              this.setState({ editingItem: item }, () =>
                this.editRef.current?.show(),
              )
          : null
      }
    />
  );

  renderAddNewForm = () => (
    <NtModal ref={this.createRef}>
      <NtIvrMenuForm
        onCancel={() => this.createRef.current?.dismiss()}
        onSave={() => {
          this.createRef.current?.dismiss();
          this.setState({ page: 1 }, () => this.handleDataFetch());
          showToast("Success", "Successfully created the new IVR menu");
        }}
      />
    </NtModal>
  );

  renderEditForm = () => (
    <NtModal ref={this.editRef}>
      {this.state.editingItem ? (
        <NtIvrMenuForm
          item={this.state.editingItem}
          onCancel={() => this.editRef.current?.dismiss()}
          onSave={() => {
            this.editRef.current?.dismiss();
            this.setState({ editingItem: null }, () => this.handleDataFetch());
            showToast("Success", "Successfully updated the IVR menu");
          }}
          onDelete={() => {
            this.editRef.current?.dismiss();
            this.setState({ editingItem: null }, () => this.handleDataFetch());
            showToast("Success", "Successfully deleted the IVR menu");
          }}
        />
      ) : null}
    </NtModal>
  );

  render() {
    const canEdit = this.handlePermissionCheck(settings.permissionTypeEdit);
    const count = this.state.totalItems || 0;
    const subtitle = `Auto-attendant menus that route callers via touch-tone options · ${count} menu${count === 1 ? "" : "s"}`;

    const actions = canEdit ? (
      <NtPageActionButton
        icon="plus"
        title="New IVR Menu"
        variant="primary"
        onPress={() => this.createRef.current?.show()}
      />
    ) : null;

    return (
      <NtRootPageContainer
        pageTitle="IVR Menus"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: 35 }}
              columns={ivrcsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="ivr-menus"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAddNewForm()}
        {this.renderEditForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default IvrController;
